import React, { useEffect, useMemo, useState } from 'react';

import { format, getZonedDate } from '@eika-infrastruktur/date';
import { formatAccountNumber } from '@eika-infrastruktur/format';
import { postMessageRN, runningInRNWebView, WebToRNAction } from '@eika-infrastruktur/mobilbank-lib';
import { LinkButton } from '@eika/button';
import { IconTypes } from '@eika/icon';

import { useConfig } from '../../../defaultConfig';
import { useAccounts } from '../../../fetchHooks/useAccount';
import { useNextBankingDay } from '../../../fetchHooks/useBankingDays';
import { useValidateIban } from '../../../fetchHooks/useCrossBorderBankValidation';
import type { AccountId, TransactionDetails } from '../../../generated-types';
import useTransactionActor from '../../../hooks/useTransactionActor';
import useTranslation from '../../../hooks/useTranslation';
import { getTransactionPdfUrl } from '../../../services/accountService';
import ReservationWarning from './ReservationWarning';
import TransactionActorAddressField from './TransactionActorAddressField';
import TransactionAmount from './TransactionAmount';
import TransactionApprovers from './TransactionApprovers';
import TransactionLine from './TransactionLine';

import './TransactionDetailsBody.scss';

export interface Props {
    transactionDetails: TransactionDetails;
    accountId?: AccountId;
}

const dateOnly = 'dd.MM.yyyy';

const TransactionDetailsBody: React.FC<Props> = ({ transactionDetails, accountId }) => {
    const isInterestOrFee = transactionDetails?.type === 'INTEREST' || transactionDetails?.type === 'FEE';
    const isCrossborderPayment = transactionDetails?.type === 'INTERNATIONAL';
    const translated = useTranslation();
    const { featureToggles } = useConfig();
    const [transactionDetailsType, setTransactionDetailsType] = useState(transactionDetails?.type);

    const { data: accounts } = useAccounts();
    const checkOcr = transactionDetails?.type !== 'INTERNATIONAL';
    const { transactionActor: toAccount, isLoading: toAccountIsLoading } = useTransactionActor(
        transactionDetails?.to,
        checkOcr,
    );
    const { transactionActor: fromAccount, isLoading: fromAccountIsLoading } = useTransactionActor(
        transactionDetails?.from,
        checkOcr,
    );

    const { data: nextBankingDay, isLoading: nextBankingDayIsLoading } = useNextBankingDay(
        getZonedDate(transactionDetails?.createdDate),
    );

    const statusLabel = useMemo(() => {
        if (transactionDetails?.status && transactionDetails.status !== 'BOOKED') {
            return translated.transactionStatus[transactionDetails.status];
        }
        return undefined;
    }, [transactionDetails, translated.transactionStatus]);

    const transactionLabel = useMemo(() => {
        if (transactionDetailsType && transactionDetailsType !== 'UNKNOWN') {
            return translated.transactionTypes[transactionDetailsType];
        }
        return undefined;
    }, [transactionDetailsType, translated.transactionTypes]);

    const onClick = () => {
        if (!accountId || !transactionDetails) return;
        const url = getTransactionPdfUrl(accountId, transactionDetails?.transactionId);
        if (runningInRNWebView) {
            postMessageRN({
                action: WebToRNAction.OPEN_PDF,
                payload: {
                    url,
                    title: `transaksjonsdetaljer-${transactionDetails.transactionReference}`,
                },
            });
        } else {
            window.open(url, 'menubar=no,location=no,resizable=yes,scrollbars=yes,status=yes,noopener');
        }
    };

    useEffect(() => {
        setTransactionDetailsType(transactionDetails?.type);
    }, [transactionDetails?.type]);

    useEffect(() => {
        if (transactionDetailsType === 'PAYMENT_OR_TRANSFER' && accounts) {
            const toAccountIsOwn = accounts.find(
                (ownAccount) => ownAccount.accountNumber.value === transactionDetails?.to?.accountNumber?.value,
            );
            const fromAccountIsOwn = accounts.find(
                (ownAccount) => ownAccount.accountNumber.value === transactionDetails?.from?.accountNumber?.value,
            );

            if (toAccountIsOwn && fromAccountIsOwn) {
                setTransactionDetailsType('TRANSFER');
            } else {
                setTransactionDetailsType('PAYMENT');
            }
        }
    }, [
        accounts,
        toAccount,
        transactionDetails?.from?.accountNumber,
        transactionDetails?.to?.accountNumber,
        transactionDetailsType,
    ]);

    const { data: bankInfo } = useValidateIban(
        transactionDetails?.to?.iban,
        featureToggles.enableAdditionalInfoTransactions,
    );

    return (
        <div className="transaction-details">
            <TransactionAmount
                value={transactionDetails.transactionAmount}
                instructedAmount={transactionDetails.instructedAmount}
                instructedCurrency={transactionDetails.instructedCurrency}
                typeLabel={[transactionLabel, statusLabel].filter(Boolean).join(' – ')}
                eFakturaId={transactionDetails.eFakturaId}
            />
            <div className="transaction-details__details">
                {/* From account */}
                <TransactionLine
                    show={!isInterestOrFee}
                    label={translated.transactionDetails.from}
                    isLoading={fromAccountIsLoading}
                    value={fromAccount?.name}
                    customValue={
                        fromAccount && (
                            <span className="transaction-details__payer-account-number">
                                {fromAccount.iban?.value ?? formatAccountNumber(fromAccount.accountNumber)}
                            </span>
                        )
                    }
                    showInfoMsgIfEmptyValue={transactionDetails?.status === 'RESERVED'}
                />
                {/* To account */}
                <TransactionLine
                    show={transactionDetails?.type !== 'FEE'}
                    label={translated.transactionDetails.to}
                    isLoading={toAccountIsLoading}
                    value={toAccount?.name}
                    customValue={
                        toAccount && (
                            <span className="transaction-details__payer-account-number">
                                {toAccount.iban?.value ?? formatAccountNumber(toAccount.accountNumber)}
                            </span>
                        )
                    }
                    showInfoMsgIfEmptyValue={transactionDetails?.status === 'RESERVED'}
                />
                {/* Dato */}
                <TransactionLine
                    value={transactionDetails?.createdDate}
                    label={translated.transactionDetails.date}
                    formatter={(date) => format(date, dateOnly)}
                />
                {/* Bokført */}
                <TransactionLine
                    value={transactionDetails?.bookingDate}
                    label={translated.transactionDetails.bookingDate}
                    formatter={(date) => format(date, dateOnly)}
                    showInfoMsgIfEmptyValue={transactionDetails?.status === 'RESERVED'}
                />
                {featureToggles.enableAdditionalInfoTransactions && isCrossborderPayment && (
                    <>
                        <TransactionActorAddressField
                            label={translated.transactionDetails.receiver.address}
                            address={transactionDetails?.to?.address}
                        />
                        {bankInfo?.bank?.name && (
                            <TransactionLine
                                label={translated.transactionDetails.receiver.bank}
                                value={bankInfo.bank.name}
                            />
                        )}
                        {bankInfo?.bank?.bic && (
                            <TransactionLine
                                label={translated.transactionDetails.receiver.bicOrSwift}
                                value={bankInfo.bank.bic.value}
                            />
                        )}
                    </>
                )}
                {/* Bankens arkivreferanse */}
                <TransactionLine
                    value={transactionDetails?.transactionReference}
                    label={translated.transactionDetails.bankReferenceNumber}
                />
                {/* Forventet oppdatert */}
                <TransactionLine
                    show={
                        featureToggles.enableTransactionDetailsReservedFields &&
                        transactionDetails?.status === 'RESERVED' &&
                        !!nextBankingDay
                    }
                    value={format(nextBankingDay, dateOnly)}
                    label={translated.expectedBookBalanceDate}
                    isLoading={nextBankingDayIsLoading}
                />
                {/* KID */}
                <TransactionLine value={transactionDetails?.kid?.value} label={translated.transactionDetails.kid} />
                {/* Note */}
                <TransactionLine
                    value={transactionDetails?.text}
                    label={translated.transactionDetails.message}
                    showInfoMsgIfEmptyValue={transactionDetails?.status === 'RESERVED'}
                />
                {/* Own text */}
                {transactionDetails?.text.toLowerCase() !== transactionDetails?.ownText?.toLowerCase() && (
                    <TransactionLine
                        value={transactionDetails?.ownText}
                        label={translated.transactionDetails.ownMessage}
                    />
                )}
                {/* Purpose */}
                <TransactionLine
                    show={
                        !!transactionDetails.purpose &&
                        transactionDetails.purpose.toLowerCase() !== transactionDetails.text.toLocaleLowerCase()
                    }
                    label={translated.transactionDetails.purpose}
                    value={transactionDetails.purpose}
                />
                <TransactionLine
                    show={!!transactionDetails.categoryDetails}
                    label={translated.transactionDetails.category}
                    value={transactionDetails.categoryDetails}
                />

                {transactionDetailsType && transactionDetailsType === 'COMBINED_POSTING' && (
                    <TransactionLine
                        value={translated.transactionDetails.multipleTransactionsDetails}
                        label={translated.details}
                    />
                )}
                <TransactionApprovers approvers={transactionDetails?.approvers} />
            </div>
            {featureToggles.showTransactionPdfLink && transactionDetails?.status !== 'RESERVED' && (
                <LinkButton
                    onClick={onClick}
                    icon={IconTypes.NEDLASTING_36}
                    iconOnRightSide
                    className="transaction-details__pdf-download"
                >
                    {translated.transactionDetails.download}
                </LinkButton>
            )}
            <ReservationWarning type={transactionDetails?.status} />
        </div>
    );
};

export default TransactionDetailsBody;
